/**
 * THE OFFER ARTEMIS DICTATES AND THE SELECTION THE CUSTOMER MAKES — the shapes only (15 Oct 2026).
 * Pure zod, imported by `customer-pricing.ts` (the released copy carries the offer) and by
 * `customer-selection.ts` (the arithmetic). See the latter for the words that explain them.
 */
import { z } from 'zod';
const money = z.number().finite().min(-1e9).max(1e9);
const pt = z.object({ x: z.number().finite(), y: z.number().finite() });
export const OfferColour = z.object({ id: z.string().max(160), name: z.string().max(160), swatch: z.string().max(400).nullable().default(null) });
export const OfferProduct = z.object({
    id: z.string().max(80), name: z.string().max(200),
    type: z.enum(['hard_floor', 'carpet']), sub_type: z.string().max(60).default(''),
    /** Artemis cost per m² of board or carpet. */
    board: money,
    /** Charged area = measured × waste (hard floor); carpet charges the roll run instead. */
    waste: z.number().min(1).max(2).default(1.05),
    plank: z.object({ length_m: z.number().positive(), width_m: z.number().positive() }).nullable().default(null),
    roll_width_m: z.number().positive().max(20).nullable().default(null),
    nosing_price: money.nullable().default(null),
    colours: z.array(OfferColour).max(60).default([]),
    recommended: z.boolean().default(false),
    /** Selling language: what this board is. Never a cost. */
    intro: z.string().max(400).default(''),
    /** The rooms this product may go in, by room id; null = any room (Jacky, 15 Oct: "carpet option only 3 bedroom upstair"). */
    rooms: z.array(z.string().max(80)).max(200).nullable().default(null),
});
export const OfferRoom = z.object({ id: z.string().max(80), name: z.string().max(120), floor: z.string().max(80), area_m2: z.number().nonnegative(), polygon: z.array(pt).min(3).max(500) });
export const OfferPreset = z.object({ id: z.string().max(40), name: z.string().max(120), description: z.string().max(300).default(''), rooms: z.record(z.string(), z.string()) });
export const OfferRules = z.object({
    margin_rate: z.number().min(0).max(0.9), labour_per_m2: money,
    trims: z.object({ piece_m: z.number().positive(), spare_pieces: z.number().int().nonnegative() }),
    stairs: z.object({ labour_per_step: money, no_riser_factor: z.number().positive().max(1), planks_per_step: z.number().positive(), nosing_share: z.number().positive(), plank_m2_default: z.number().positive() }),
    skirting: z.object({ wastage: z.number().min(1), length_m: z.number().positive(), labour_per_lm: money, sizes: z.array(z.object({ size: z.string().max(10), material_per_lm: money })).min(1) }),
    architrave: z.object({ size: z.string().max(10), lengths_standard: z.number().int().positive(), lengths_wide: z.number().int().positive(), wide_over_m: z.number().positive(), labour_per_door: money, material_per_length: money }),
    disposal: z.object({ per_m2: money }),
});
export const Offer = z.object({
    products: z.array(OfferProduct).min(1).max(60),
    rooms: z.array(OfferRoom).max(200),
    underlays: z.array(z.object({ floor: z.string().max(80), name: z.string().max(200), cost: money })).max(12),
    carpet_underlay: z.object({ name: z.string().max(200), cost: money }),
    trims: z.array(z.object({ profile: z.string().max(60), run_m: z.number().nonnegative(), piece_cost: money })).max(12),
    steps: z.number().int().nonnegative(), stair_no_riser: z.boolean().default(false),
    skirting_run_lm: z.number().nonnegative(),
    /** Rooms the skirting run leaves out, by name — shown to the customer as the reason the figure is what it is. */
    skirting_excluded: z.array(z.string().max(120)).default([]),
    doors: z.number().int().nonnegative(), door_lengths: z.number().int().nonnegative(),
    /** Skirting / architrave profiles the customer may pick (the shape only; stock is Artemis's call). */
    profiles: z.array(z.object({ id: z.string().max(60), label: z.string().max(120), visual: z.enum(['bevelled', 'half_splayed', 'pencil_round', 'bullnose', 'colonial', 'lambs_tongue']) })).max(12).default([]),
    presets: z.array(OfferPreset).max(6).default([]),
    rules: OfferRules,
});
export const CustomerSelection = z.object({
    rooms: z.record(z.string(), z.object({ product: z.string().max(80), orientation: z.enum(['x', 'y']).nullable().default(null) })).default({}),
    /** One colour per product for the whole job (Jacky: "colour is for whole job"), by colour id. */
    colours: z.record(z.string(), z.string().max(160).nullable()).default({}),
    skirting: z.object({ size: z.string().max(10).nullable().default(null), profile: z.string().max(60).nullable().default(null) }).default({ size: null, profile: null }),
    architrave: z.object({ on: z.boolean().default(false), profile: z.string().max(60).nullable().default(null) }).default({ on: false, profile: null }),
    disposal: z.enum(['skip', 'artemis']).default('skip'),
});
//# sourceMappingURL=customer-offer.js.map